import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  type RefObject,
} from "react";
import { AnimatePresence, motion, MotionConfig } from "motion/react";
import { Ticket, X } from "lucide-react";
import { notificationTypeIcons } from "@/components/notifications/notification-type-icons";
import { NotificationMarkdown } from "@/components/notifications/notification-markdown";
import { IconContainer } from "@/components/ui/icon-container";

interface PopupItem {
  id: string;
  type: string;
  title: string;
  message?: string;
  addedAt: number;
}

export interface NotificationPopupHandle {
  addNotification: (notification: {
    type: string;
    title: string;
    message?: string;
  }) => void;
}

interface NotificationPopupProps {
  anchorRef: RefObject<HTMLButtonElement | null>;
}

const AUTO_DISMISS_MS = 5_000;
const MAX_VISIBLE = 3;
const POPUP_WIDTH = 320; // w-80

function computePosition(anchor: HTMLButtonElement | null) {
  const rect = anchor?.getBoundingClientRect();
  if (!rect) return null;

  const anchorCenterX = rect.left + rect.width / 2;
  const top = rect.bottom + 8;

  const left = Math.min(
    Math.max(8, anchorCenterX - POPUP_WIDTH / 2),
    window.innerWidth - POPUP_WIDTH - 8
  );
  const arrowLeft = anchorCenterX - left - 8;

  return { top, left, arrowLeft };
}

function useAnchorPosition(
  anchorRef: RefObject<HTMLButtonElement | null>,
  active: boolean
) {
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    arrowLeft: number;
  } | null>(null);

  useEffect(() => {
    if (!active) {
      setPos(null);
      return;
    }

    const update = () => setPos(computePosition(anchorRef.current));
    update();

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorRef, active]);

  return pos;
}

export const NotificationPopup = forwardRef<
  NotificationPopupHandle,
  NotificationPopupProps
>(function NotificationPopup({ anchorRef }, ref) {
  const [items, setItems] = useState<PopupItem[]>([]);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const addNotification = useCallback(
    (notification: { type: string; title: string; message?: string }) => {
      const id = crypto.randomUUID();
      const item: PopupItem = {
        id,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        addedAt: Date.now(),
      };

      setItems((prev) => [item, ...prev]);

      setTimeout(() => {
        removeItem(id);
      }, AUTO_DISMISS_MS);
    },
    [removeItem]
  );

  useImperativeHandle(ref, () => ({ addNotification }), [addNotification]);

  const visibleItems = items.slice(0, MAX_VISIBLE);
  const overflowCount = Math.max(0, items.length - MAX_VISIBLE);

  const pos = useAnchorPosition(anchorRef, items.length > 0);

  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence>
        {items.length > 0 && pos && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed z-[60] w-80"
            style={{ top: pos.top, left: pos.left }}
          >
            {/* Arrow pointing up, aligned to bell center */}
            <div
              className="border-b-border size-0 border-x-[8px] border-b-[8px] border-x-transparent"
              style={{ marginLeft: pos.arrowLeft }}
            />

            <div className="bg-popover border-border overflow-hidden rounded-xl border shadow-lg">
              <AnimatePresence initial={false} mode="popLayout">
                {visibleItems.map((item) => {
                  const Icon = notificationTypeIcons[item.type] ?? Ticket;

                  return (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{
                        opacity: { duration: 0.15, ease: "easeOut" },
                        scale: { duration: 0.15, ease: "easeOut" },
                        layout: { duration: 0.2, ease: "easeOut" },
                      }}
                    >
                      <div className="flex items-start gap-3 border-b p-3 last:border-b-0">
                        <IconContainer icon={Icon} size="sm" variant="accent" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">
                            {item.title}
                          </p>
                          {item.message && (
                            <div className="mt-0.5 line-clamp-2">
                              <NotificationMarkdown
                                content={item.message}
                                inline
                              />
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          aria-label="Dismiss notification"
                          className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {overflowCount > 0 && (
                <div className="text-muted-foreground border-t px-3 py-1.5 text-center text-xs">
                  +{overflowCount} more
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
});
